/**
 * SessionDetail - Vista detallada de sesión con evaluación guardada
 * Muestra la evaluación completa de una sesión previamente completada
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getSafeToken } from '../../utils/tokenHelper';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';
import EvaluationReport from './components/EvaluationReport';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEvaluation();
  }, [sessionId]);

  const fetchEvaluation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = getSafeToken();

      const response = await fetch(`${VERTEX_API_BASE}/v4/session/${sessionId}/evaluation`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al cargar evaluación');
      }

      if (data.success) {
        setEvaluation(data.evaluation);
      } else {
        throw new Error(data.message || 'No se pudo cargar la evaluación');
      }

    } catch (error) {
      console.error('[SessionDetail] Error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando evaluación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-50 via-white to-pink-50 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/vertex/analytics')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a Analytics
          </button>
          
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error al cargar evaluación</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/vertex/analytics')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Volver a Analytics
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-purple-50 via-white to-pink-50 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/vertex/analytics')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a Analytics
        </button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Evaluación de Sesión</h1>
          <p className="text-gray-600">ID: {sessionId}</p>
        </div>

        {evaluation && (
          <EvaluationReport 
            sessionId={sessionId}
            onClose={() => navigate('/vertex/analytics')}
            hideHeader={true}
          />
        )}
      </div>
    </div>
  );
};

export default SessionDetail;
